/** @jsxImportSource @emotion/react */

import { Field, FieldProps, useFormikContext } from 'formik';

import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import UDFormLabel from '../label';
import { ImageList } from './components/image-list';
import UDDropZone from './components/ud-drop-zone';
import { useImageManagement } from './hooks/use-image-management';
import { UDFormDropZoneProps } from './types';
import { fileToImage } from './utils/file-to-image';
import Modal from 'ud-ui/components/modal';

const ACCEPTED_FORMATS = 'image/png,image/jpeg,image/jpg,image/webp';

const DeleteImageModal = ({
  isOpen,
  onClose,
  onDelete,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  isLoading?: boolean;
}) => {
  return (
    <Modal show={isOpen} onClose={onClose}>
      <Modal.Body>
        <UDText type="headline" className="mb-2 px-10" style={{ textAlign: 'center' }}>
          Удалить изображение?
        </UDText>
        <UDText type="subhead" style={{ textAlign: 'center' }}>
          После сохранения изображение будет удалено.
        </UDText>
      </Modal.Body>
      <Modal.Footer>
        <UDButton
          className="d-flex align-items-center justify-content-center w-50 mb-3"
          style={{ width: 144 }}
          variant="primary"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isLoading}
          loading={isLoading}
        >
          <span>Да</span>
        </UDButton>
        <UDButton
          className="d-flex align-items-center justify-content-center w-50 mb-3"
          style={{ width: 144 }}
          variant="primary"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <span>Нет</span>
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
};

const UDFormDropZone: React.FC<Omit<UDFormDropZoneProps, 'value'>> = ({
  name,
  containerProps,
  label,
  labelProps,
  formProps,
  onChangeInterceptor = (value: Blob) => value,
  onDeleteImage,
  multiple,
  enableDragAndDrop = false,
  disabled = false,
  ...otherDropZoneProps
}) => {
  const { setFieldValue, values } = useFormikContext<Record<string, any>>();
  const currentValue = values[name];

  const { images, handleFileChange, handleDelete, handleDragEnd } = useImageManagement({
    name,
    multiple,
    currentValue,
    setFieldValue,
    onChangeInterceptor,
  });

  // const { defaultOpen: openDeleteModal, close: closeDeleteModal, Modal: DeleteModal } = useModal();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const openDeleteModal = useCallback(() => {
    setIsOpenModal(true);
  }, []);
  const closeDeleteModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const [imageToDelete, setImageToDelete] = React.useState<number | null>(null);

  const handleDeleteWithConfirmation = (index: number) => {
    if (disabled) return;
    setImageToDelete(index);
    openDeleteModal();
  };

  const confirmDelete = () => {
    if (imageToDelete !== null) {
      handleDelete(imageToDelete);
      closeDeleteModal();
      setImageToDelete(null);
    }
  };

  const handleFileChangeWithDisabled = (files: Array<File>) => {
    if (disabled) return;
    handleFileChange(files);
  };

  const handleDragEndWithDisabled = (result: any) => {
    if (disabled) return;
    handleDragEnd(result);
  };

  const renderContent = () => {
    return (
      <>
        <ImageList
          images={images.map((img) => (img instanceof File ? fileToImage(img) : img))}
          enableDragAndDrop={enableDragAndDrop && !disabled}
          onDelete={handleDeleteWithConfirmation}
          onDeleteImage={onDeleteImage}
          multiple={multiple}
          onDragEnd={handleDragEndWithDisabled}
          disabled={disabled}
        />
        <DeleteImageModal isOpen={isOpenModal} onClose={closeDeleteModal} onDelete={confirmDelete} />
      </>
    );
  };

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps<any>) => {
        const isErrorVisible = meta.touched && meta.error && label;
        return (
          <div {...containerProps}>
            {label && (
              <UDFormLabel {...labelProps} className={classNames('mb-2', labelProps?.className)}>
                {label}
              </UDFormLabel>
            )}
            {isErrorVisible && (
              <UDText type="caption-1" className="color-CaseError">
                {meta.error}
              </UDText>
            )}
            <div {...formProps}>
              <UDDropZone
                accept={ACCEPTED_FORMATS}
                {...otherDropZoneProps}
                {...field}
                onChange={handleFileChangeWithDisabled}
                multiple={multiple}
                disabled={disabled}
                dropZoneOptions={{
                  ...(otherDropZoneProps.dropZoneOptions || {}),
                  disabled,
                  noClick: disabled,
                  noDrag: disabled,
                  noKeyboard: disabled,
                }}
              />
              <span className="css-1 text footnote color-SurfaceRomanSilver30 mt-2">Формат: png, jpg</span>
              {renderContent()}
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export default UDFormDropZone;
